import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { EyeIcon, HeartIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';

const ProductCard = ({ product, viewMode = 'grid', selectable = false, isSelected = false, onToggleSelect }) => {
  const isListView = !selectable && viewMode === 'list';
  const [isHovered, setIsHovered] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);

  const handleImageError = () => {
    setImageError(true);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getImageUrl = () => {
    if (imageError || !product.photo) {
      return 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80';
    }
    return product.photo;
  };

  const getAllProductPhotos = () => {
    // Get all photos from the product
    const photos = [];
    
    // Add photos from the photos array (new multiple photos feature)
    if (product.photos && product.photos.length > 0) {
      photos.push(...product.photos);
    }
    
    // Add single photo if it exists and not already in photos array
    if (product.photo && !photos.includes(product.photo)) {
      photos.push(product.photo);
    }
    
    // If no photos found, return fallback image
    if (photos.length === 0) {
      return ['https://images.unsplash.com/photo-1518709268805-4e9042af2176?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'];
    }
    
    return photos;
  };

  const handlePhotoSelect = (index) => {
    setSelectedPhotoIndex(index);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="group"
    >
      <div
        className={`product-card bg-white overflow-hidden border cursor-pointer transition-all duration-200 ${
          isListView ? 'flex flex-row rounded-2xl hover:shadow-medium' : 'flex flex-col'
        } ${
          selectable
            ? `rounded-lg shadow-sm hover:shadow-md h-[480px] ${
                isSelected
                  ? 'border-primary-600 ring-1 ring-primary-600 shadow-md'
                  : 'border-slate-200 hover:border-slate-300'
              }`
            : isListView
              ? `border-slate-200/80 hover:border-primary-200 ${
                  isSelected ? 'border-primary-500 ring-2 ring-primary-500' : ''
                }`
              : `rounded-2xl border-slate-200/80 hover:shadow-medium hover:border-primary-200 hover:-translate-y-0.5 ${
                  isSelected ? 'border-primary-500 ring-2 ring-primary-500' : ''
                }`
        }`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => {
          setSelectedPhotoIndex(0);
          setShowDetailsModal(true);
        }}
      >
        {selectable && isSelected && (
          <div className="h-1 bg-primary-600 shrink-0" />
        )}
        {/* Product Image */}
        <div className={`relative overflow-hidden shrink-0 ${
          selectable
            ? 'h-52 bg-slate-50 border-b border-slate-100'
            : isListView
              ? 'w-36 sm:w-44 md:w-48 aspect-square bg-slate-50 border-r border-slate-100'
              : 'aspect-[4/3] bg-slate-50'
        }`}>
          <img
            src={getImageUrl()}
            alt={product.name}
            className={`w-full h-full ${
              selectable || isListView ? 'object-contain p-3' : 'object-contain p-4 image-zoom'
            }`}
            onError={handleImageError}
          />
          
          {/* Image Overlay */}
          {!selectable && (
            <div className={`absolute inset-0 bg-black/20 transition-opacity duration-300 ${
              isHovered ? 'opacity-100' : 'opacity-0'
            }`} />
          )}

          {selectable && (
            <label
              className={`absolute top-3 right-3 z-20 flex items-center justify-center w-7 h-7 rounded border-2 cursor-pointer transition-colors ${
                isSelected
                  ? 'bg-primary-600 border-primary-600 text-white'
                  : 'bg-white border-slate-300 text-transparent hover:border-primary-400'
              }`}
              onClick={(event) => event.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onToggleSelect?.(product)}
                className="sr-only"
                aria-label={`Select ${product.name}`}
              />
              {isSelected && (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </label>
          )}

          {/* Brand Badge */}
          {(product.brandName || product.brand) && (
            <div className="absolute top-3 left-3">
              <span className={`font-semibold uppercase tracking-wide text-white ${
                selectable
                  ? 'px-2 py-0.5 bg-secondary-800 text-[10px] rounded-sm'
                  : 'px-2 py-1 bg-primary-600 text-xs rounded-full'
              }`}>
                {product.brandName || product.brand}
              </span>
            </div>
          )}

          {/* Category Badge */}
          {(product.categoryName || product.category) && !selectable && (
            <div className="absolute bottom-3 left-3">
              <span className="px-2 py-1 bg-gray-800/80 text-white text-xs font-medium rounded-full">
                {product.categoryName || product.category}
              </span>
            </div>
          )}
        </div>

        {/* Product Details */}
        <div className={`flex-1 flex flex-col min-h-0 ${selectable ? 'p-4' : isListView ? 'p-4 sm:p-5 justify-center' : 'p-4'}`}>
          {selectable && (product.categoryName || product.category) && (
            <p className="text-[10px] font-semibold uppercase tracking-wider text-primary-600 mb-1.5">
              {product.categoryName || product.category}
            </p>
          )}
          <h3
            className={`font-semibold line-clamp-2 transition-colors duration-200 ${
              selectable
                ? 'text-sm text-slate-900 leading-snug mb-2'
                : 'text-slate-900 mb-1.5 group-hover:text-primary-600 text-sm sm:text-base'
            }`}
          >
            {product.name}
          </h3>

          {product.description && !selectable && (
            <p className={`text-xs sm:text-sm text-slate-500 mb-3 ${isListView ? 'line-clamp-2' : 'line-clamp-3'}`}>
              {product.description}
            </p>
          )}

          {product.description && selectable && (
            <p className="text-xs text-slate-500 line-clamp-2 mb-2 leading-relaxed">
              {product.description}
            </p>
          )}

          {/* Features Preview */}
          {product.features && product.features.length > 0 && !selectable && (
            <div className="mt-3 flex flex-wrap gap-1 h-8">
              {product.features.slice(0, 3).map((feature, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                >
                  {feature}
                </span>
              ))}
              {product.features.length > 3 && (
                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                  +{product.features.length - 3} more
                </span>
              )}
            </div>
          )}

          <div className={`flex items-center justify-between mt-auto gap-3 ${
            selectable ? 'pt-3 border-t border-slate-100' : 'pt-2'
          }`}>
            {product.mrp && (
              <div className={`${selectable ? 'text-xs text-slate-500' : 'text-xs text-slate-500'}`}>
                <span className="line-through">{formatPrice(product.mrp)}</span>
              </div>
            )}
            {product.offerPrice && (
              <div className={`font-bold flex items-center flex-wrap justify-end gap-1 ${
                selectable ? 'text-sm text-emerald-600' : 'text-sm sm:text-base text-emerald-600'
              }`}>
                {selectable ? formatPrice(product.offerPrice) : formatPrice(product.offerPrice)}
                {!selectable && product.mrp && product.offerPrice && product.mrp > product.offerPrice && (
                  <span className="text-[10px] sm:text-xs font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-md">
                    {Math.round(((product.mrp - product.offerPrice) / product.mrp) * 100)}% off
                  </span>
                )}
              </div>
            )}
          </div>


        </div>
      </div>

      {/* Product Details Modal */}
      {showDetailsModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Product Details
              </h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Product Photo Gallery */}
                <div className="space-y-4">
                  {/* Main Photo Display */}
                  <div className="aspect-square overflow-hidden bg-gray-100 rounded-lg">
                    <img
                      src={getAllProductPhotos()[selectedPhotoIndex]}
                      alt={`${product.name} - Photo ${selectedPhotoIndex + 1}`}
                      className="w-full h-full object-cover"
                      onError={handleImageError}
                    />
                  </div>
                  
                  {/* Photo Thumbnails */}
                  {getAllProductPhotos().length > 1 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-gray-500">All Photos ({getAllProductPhotos().length})</h4>
                      <div className="grid grid-cols-4 gap-2">
                        {getAllProductPhotos().map((photo, index) => (
                          <button
                            key={index}
                            onClick={() => handlePhotoSelect(index)}
                            className={`aspect-square overflow-hidden rounded-lg border-2 transition-all duration-200 ${
                              selectedPhotoIndex === index 
                                ? 'border-primary-600 ring-2 ring-primary-200' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <img
                              src={photo}
                              alt={`${product.name} - Photo ${index + 1}`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80';
                              }}
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Brand and Category Badges */}
                  <div className="flex flex-wrap gap-2">
                    {(product.brandName || product.brand) && (
                      <span className="px-3 py-1 bg-primary-600 text-white text-sm font-medium rounded-full">
                        {product.brandName || product.brand}
                      </span>
                    )}
                    {(product.categoryName || product.category) && (
                      <span className="px-3 py-1 bg-gray-800 text-white text-sm font-medium rounded-full">
                        {product.categoryName || product.category}
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Product Information */}
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      {product.name}
                    </h2>
                    {product.description && (
                      <p className="text-gray-600 leading-relaxed">
                        {product.description}
                      </p>
                    )}
                  </div>
                  
                  {/* Product ID */}
                  {product.productId && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-1">Product ID</h4>
                      <p className="text-gray-900">{product.productId}</p>
                    </div>
                  )}
                  
                  {/* Pricing */}
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-500">Pricing</h4>
                    <div className="space-y-1">
                      {product.mrp && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">MRP:</span>
                          <span className="font-semibold text-gray-900">{formatPrice(product.mrp)}</span>
                        </div>
                      )}
                      {product.offerPrice && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Offer Price:</span>
                          <span className="font-semibold text-green-600 text-lg">{formatPrice(product.offerPrice)}</span>
                        </div>
                      )}
                      {product.mrp && product.offerPrice && product.mrp > product.offerPrice && (
                        <div className="text-sm text-green-600 font-medium">
                          Save {formatPrice(product.mrp - product.offerPrice)} ({Math.round(((product.mrp - product.offerPrice) / product.mrp) * 100)}% off)
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Features */}
                  {product.features && product.features.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Features</h4>
                      <div className="flex flex-wrap gap-2">
                        {product.features.map((feature, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Status */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-1">Status</h4>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      product.isActive !== false 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {product.isActive !== false ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  

                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default ProductCard;
